import { sdk } from "@/lib/utils/sdk"
import { CATERING_ENABLED, PREORDER_ENABLED } from "@/lib/constants/site"

/**
 * Site-wide feature toggles, owned by Medusa (Admin → Settings → Store) so
 * staff can publish or hide a surface without a storefront deploy.
 *
 * The constants in `lib/constants/site.ts` are the FALLBACK, not the source of
 * truth: when the backend cannot be reached the storefront keeps the value it
 * was built with. Both currently ship as `false`, so an outage fails closed —
 * a hidden surface stays hidden rather than appearing half-wired.
 */
export type SiteSettings = {
  cateringEnabled: boolean
}

export const FALLBACK_SITE_SETTINGS: SiteSettings = {
  cateringEnabled: CATERING_ENABLED,
}

export const SITE_SETTINGS_QUERY_KEY = ["site-settings"] as const

const normalizeBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") return value
  if (value === "true") return true
  if (value === "false") return false
  return fallback
}

export function normalizeSiteSettings(value: unknown): SiteSettings {
  const raw =
    !!value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {}

  return {
    cateringEnabled: normalizeBoolean(
      raw.cateringEnabled,
      FALLBACK_SITE_SETTINGS.cateringEnabled
    ),
  }
}

/**
 * Never throws. A feature flag that fails the whole render is worse than one
 * that falls back, and every caller here is on a page-render path.
 */
export async function fetchSiteSettings(): Promise<SiteSettings> {
  try {
    const { site_settings } = await sdk.client.fetch<{
      site_settings: unknown
    }>("/store/site-settings", { method: "GET" })

    return normalizeSiteSettings(site_settings)
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[+Bake Cafe] Failed to load site settings", error)
    }
    return FALLBACK_SITE_SETTINGS
  }
}

export const siteSettingsQueryOptions = {
  queryKey: SITE_SETTINGS_QUERY_KEY,
  queryFn: fetchSiteSettings,
  staleTime: 60_000,
}

/**
 * Opens the catering surfaces regardless of the stored toggle, so the redesign
 * can be reviewed on a Vercel preview before it is flipped on for real, and so
 * `pnpm dev` never needs a backend round-trip. Production sets neither.
 */
export const CATERING_PREVIEW_OVERRIDE: boolean =
  import.meta.env.DEV || import.meta.env.VITE_CATERING_PREVIEW === "true"

/**
 * True when the catering surfaces should render. The override is a parameter
 * rather than a direct `import.meta.env` read so the production behaviour —
 * unknown settings must stay hidden — is actually testable.
 */
export function isCateringVisible(
  settings: SiteSettings | undefined,
  previewOverride: boolean = CATERING_PREVIEW_OVERRIDE
): boolean {
  return (settings ?? FALLBACK_SITE_SETTINGS).cateringEnabled || previewOverride
}

/**
 * True when the site's own cart and checkout have something to sell: the
 * pre-order shop (build-time PREORDER_ENABLED) or the catering page. While
 * both are off, every order goes through Toast, so the cart, account and
 * sign-up entry points stay hidden — an account there can't order anything.
 * Existing customers can still reach /account/login and their order history.
 */
export function isSiteCheckoutOpen(
  settings: SiteSettings | undefined,
  previewOverride: boolean = CATERING_PREVIEW_OVERRIDE,
  preorderEnabled: boolean = PREORDER_ENABLED
): boolean {
  return preorderEnabled || isCateringVisible(settings, previewOverride)
}
